import type { ReactNode } from 'react';
import type { ImageField, LinkField, RichTextField } from '@prismicio/client';
import { asText, isFilled } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText } from '@prismicio/react';
import { OutlineCtaLink } from '@/components/common/OutlineCtaLink';
import { PrimaryGreenCtaButton } from '@/components/common/PrimaryGreenCtaButton';
import { ScrollRail } from '@/components/redesign/SideHeaderCardsRow';
import { cn } from '@/lib/utils';

export { RightIconStepCard } from './RightIconStepCard';
export { AutoScrollStepCard } from './AutoScrollStepCard';
export { AutoScrollReviewCard } from './AutoScrollReviewCard';
export { MarqueeCardsRow } from './MarqueeCardsRow';
export { ScrollingCardsRow } from './ScrollingCardsRow';

type Theme = 'light' | 'dark' | null;

export type StackedHeaderCardsRowProps = {
  sliceType: string;
  variation: string;
  theme?: Theme;
  backgroundImage?: ImageField | null;
  /** Muted / first-tone half of the heading (gray on light, green on dark). */
  title?: RichTextField | null;
  /** Dark / second-tone half of the heading (near-black on light, white on dark). */
  subtitle?: RichTextField | null;
  /** Supporting paragraph(s) below the heading. */
  description?: RichTextField | null;
  primaryCta?: LinkField | null;
  /** Optional paragraph(s) below the CTA — same styling as `description`. */
  footnote?: RichTextField | null;
  /**
   * Optional override for the cards row. Defaults to the `ScrollRail` used by
   * the horizontal-scroll variation. Variations that need a different flow
   * (e.g. an auto-scrolling marquee) pass their own wrapper here — the shell
   * only owns the header, CTA and footnote around it.
   */
  renderCardsRow?: (cards: ReactNode) => ReactNode;
  /**
   * CTA style. `outline` (default) matches the original horizontal-scroll
   * variation; `green` swaps in the shared `PrimaryGreenCtaButton` pill.
   */
  ctaTone?: 'outline' | 'green';
  /**
   * Which half of the bi-tone heading is the muted/accent one.
   * Default `title` keeps the original horizontal-scroll behavior (title →
   * gray/green, subtitle → near-black/white). Pass `subtitle` to flip it
   * (title → near-black/white, subtitle → gray/green).
   */
  mutedTone?: 'title' | 'subtitle';
  /**
   * Extra classes merged onto the description paragraph. Useful to override
   * the default multi-paragraph spacing (`[&:not(:first-child)]:mt-4`) or the
   * muted color in variations where the description is the only child of the
   * header block. Merged via `cn`, so anything you pass wins.
   */
  descriptionParagraphClassName?: string;
  /**
   * Extra classes merged onto the footnote paragraph. Same merge semantics as
   * `descriptionParagraphClassName`.
   */
  footnoteParagraphClassName?: string;
  children: ReactNode;
};

/**
 * Section shell for the "stacked-header + cards row" family: a full-width
 * split-color heading (muted title + dark subtitle, mirroring the
 * `ProblemHighlightGrid.redesign` treatment) sits on top with a supporting
 * description, a horizontal `ScrollRail` (reused from `SideHeaderCardsRow`)
 * hosts the cards, and an outlined CTA + optional footnote follow below.
 */
export function StackedHeaderCardsRow({
  sliceType,
  variation,
  theme,
  backgroundImage,
  title,
  subtitle,
  description,
  primaryCta,
  footnote,
  renderCardsRow,
  ctaTone = 'outline',
  mutedTone = 'title',
  descriptionParagraphClassName,
  footnoteParagraphClassName,
  children,
}: StackedHeaderCardsRowProps) {
  const isDark = theme === 'dark';
  const hasTitle = isFilled.richText(title);
  const hasSubtitle = isFilled.richText(subtitle);
  const hasDescription = isFilled.richText(description);
  const hasFootnote = isFilled.richText(footnote);
  const hasCta = isFilled.link(primaryCta);

  const mutedClass = isDark ? 'text-[#05f273]' : 'text-[#aaa]';
  const strongClass = isDark ? 'text-white' : 'text-[#041118]';
  const titleClass = mutedTone === 'title' ? mutedClass : strongClass;
  const subtitleClass = mutedTone === 'title' ? strongClass : mutedClass;

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={variation}
      className={cn(
        'relative overflow-hidden px-5 py-16 md:px-[30px] md:py-20 lg:py-[70px]',
        isDark ? 'bg-[#262626] text-white' : 'bg-white text-[#041118]',
      )}
    >
      {isFilled.image(backgroundImage) && (
        <PrismicNextImage
          field={backgroundImage}
          fill
          sizes="100vw"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
          fallbackAlt=""
        />
      )}

      <div className="relative mx-auto flex w-full max-w-[1280px] flex-col gap-9">
        {(hasTitle || hasSubtitle || hasDescription) && (
          <div className="flex w-full flex-col gap-9">
            {(hasTitle || hasSubtitle) && (
              <h2 className="text-balance break-words text-[28px] font-medium leading-[1.2] md:text-[42px]">
                {hasTitle && (
                  <span className={titleClass}>{asText(title)}</span>
                )}
                {hasTitle && hasSubtitle && <br />}
                {hasSubtitle && (
                  <span className={subtitleClass}>{asText(subtitle)}</span>
                )}
              </h2>
            )}
            {hasDescription && (
              <ParagraphBlock
                field={description}
                isDark={isDark}
                paragraphClassName={descriptionParagraphClassName}
              />
            )}
          </div>
        )}

        {/*
          Card width per breakpoint tuned so only one full card is visible at
          a time (the next card peeks to telegraph the horizontal scroll):
            • Mobile keeps the rail's default 300px fixed width so phones
              still show the classic peek pattern.
            • Tablet range (sm..lg, i.e. 640–1023px) uses 68% so the text
              column has room to lay out before the aspect-square icon
              forces it to wrap too aggressively — a straight 60% here was
              making cards taller than the text needed and clipping content
              at the bottom.
            • Laptop / desktop (lg and up) drops back to the original 60%.
          `sm:max-w-none` removes the rail's default 440px cap so the wider
          percentages actually take effect.
        */}
        {renderCardsRow ? (
          renderCardsRow(children)
        ) : (
          <ScrollRail cardClassName="sm:w-[85%] sm:max-w-none lg:w-[70%]">
            {children}
          </ScrollRail>
        )}

        {hasCta && (
          <div>
            {ctaTone === 'green' ? (
              <PrimaryGreenCtaButton field={primaryCta} />
            ) : (
              <OutlineCtaLink
                field={primaryCta}
                tone={isDark ? 'dark' : 'light'}
              />
            )}
          </div>
        )}

        {hasFootnote && (
          <div className="max-w-[900px]">
            <ParagraphBlock
              field={footnote}
              isDark={isDark}
              paragraphClassName={footnoteParagraphClassName}
            />
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Description and footnote share the same paragraph treatment (size, weight,
 * leading, muted color), so one small serializer wrapper keeps them in sync.
 */
function ParagraphBlock({
  field,
  isDark,
  paragraphClassName,
}: {
  field: RichTextField;
  isDark: boolean;
  paragraphClassName?: string;
}) {
  return (
    <PrismicRichText
      field={field}
      components={{
        paragraph: ({ children }) => (
          <p
            className={cn(
              'text-[16px] font-light leading-[1.5] md:text-[18px] [&:not(:first-child)]:mt-4',
              isDark ? 'text-white/75' : 'text-[#041118]',
              paragraphClassName,
            )}
          >
            {children}
          </p>
        ),
      }}
    />
  );
}
